import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Course } from '../../models/course';
import { CoursesService } from '../../services/courses.service';

interface MemberInfo {
  id: string;
  name: string;
}

interface ProgressCourse {
  courseId: string;
  courseName: string;
  status: 'pending' | 'processing' | 'success' | 'warning' | 'error';
  statusCode?: number;
  message?: string;
}

interface ProgressItem {
  memberId: string;
  memberName: string;
  courses: ProgressCourse[];
}

@Component({
  selector: 'app-bulk-assign-dialog',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bulk-assign-dialog.component.html',
  styleUrls: ['./bulk-assign-dialog.component.scss']
})
export class BulkAssignDialogComponent {
  @Input() members: MemberInfo[] = [];
  @Input() courses: Course[] = [];
  @Input() coursesLoading: boolean = false;
  @Output() closed = new EventEmitter<void>();
  @Output() completed = new EventEmitter<void>();

  // Selection phase
  readonly selectedMembers = signal<Set<string>>(new Set());
  readonly selectedCourses = signal<Set<string>>(new Set());
  readonly selectionError = signal<string>('');

  // Progress phase
  readonly isProcessing = signal<boolean>(false);
  readonly progressItems = signal<ProgressItem[]>([]);
  readonly processingComplete = signal<boolean>(false);

  constructor(private coursesService: CoursesService) {}

  toggleMember(memberId: string): void {
    const set = new Set(this.selectedMembers());
    if (set.has(memberId)) {
      set.delete(memberId);
    } else {
      set.add(memberId);
    }
    this.selectedMembers.set(set);
  }

  toggleCourse(courseId: string): void {
    const set = new Set(this.selectedCourses());
    if (set.has(courseId)) {
      set.delete(courseId);
    } else {
      set.add(courseId);
    }
    this.selectedCourses.set(set);
  }

  selectAllMembers(): void {
    const allIds = new Set(this.members.map(m => m.id));
    this.selectedMembers.set(allIds);
  }

  selectAllCourses(): void {
    const allIds = new Set(this.courses.map(c => c.COURSE_ID));
    this.selectedCourses.set(allIds);
  }

  clearSelections(): void {
    this.selectedMembers.set(new Set());
    this.selectedCourses.set(new Set());
  }

  validateAndProceed(): void {
    this.selectionError.set('');

    if (this.selectedMembers().size === 0) {
      this.selectionError.set('Please select at least one member.');
      return;
    }

    if (this.selectedCourses().size === 0) {
      this.selectionError.set('Please select at least one course.');
      return;
    }

    // Reset completion state before starting
    this.processingComplete.set(false);
    
    // Start processing
    this.processAssignments();
  }

  async processAssignments(): Promise<void> {
    const members = Array.from(this.selectedMembers());
    const courses = Array.from(this.selectedCourses());

    // Build progress structure
    const items: ProgressItem[] = members.map(memberId => {
      const member = this.members.find(m => m.id === memberId);
      return {
        memberId,
        memberName: member?.name || memberId,
        courses: courses.map(courseId => {
          const course = this.courses.find(c => c.COURSE_ID === courseId);
          return {
            courseId,
            courseName: course?.COURSE_NAME || courseId,
            status: 'pending' as const,
            message: undefined
          };
        })
      };
    });

    this.progressItems.set(items);
    this.isProcessing.set(true);

    // Process user by user
    for (let i = 0; i < items.length; i++) {
      const userItem = items[i];

      // Process each course for this user
      for (let j = 0; j < userItem.courses.length; j++) {
        const courseItem = userItem.courses[j];

        // Update to processing
        courseItem.status = 'processing';
        this.progressItems.set([...items]);

        try {
          // Call assign API
          await firstValueFrom(
            this.coursesService.assignCourse(userItem.memberId, courseItem.courseId)
          );

          // Success
          courseItem.status = 'success';
          courseItem.statusCode = 200;
          courseItem.message = 'Assigned successfully';
        } catch (error: any) {
          // Capture status code from error object
          const statusCode = error?.status || 500;
          courseItem.statusCode = statusCode;
          
          // Categorize error and set appropriate status
          if (statusCode === 409) {
            courseItem.status = 'warning';  // Already assigned
            courseItem.message = error?.message || error?.error?.message || 'Already assigned';
          } else if (statusCode === 400) {
            courseItem.status = 'error';
            courseItem.message = error?.message || error?.error?.message || 'Bad request';
          } else if (statusCode >= 500) {
            courseItem.status = 'error';
            courseItem.message = error?.message || error?.error?.message || 'Server error';
          } else {
            courseItem.status = 'error';
            courseItem.message = error?.message || error?.error?.message || 'Assignment failed';
          }
        }

        // Update UI
        this.progressItems.set([...items]);
      }
    }

    // All done - mark as complete
    this.processingComplete.set(true);

    // Emit completion to trigger cache refresh in parent
    // But do NOT auto-close dialog - let user manually close
    this.completed.emit();
  }

  countByStatus(status: 'success' | 'warning' | 'error'): number {
    let count = 0;
    for (const item of this.progressItems()) {
      for (const course of item.courses) {
        if (course.status === status) {
          count++;
        }
      }
    }
    return count;
  }

  cancel(): void {
    // Only allow closing if not processing OR if processing is complete
    if (!this.isProcessing() || this.processingComplete()) {
      this.clearSelections();
      this.selectionError.set('');
      this.isProcessing.set(false);
      this.processingComplete.set(false);
      this.progressItems.set([]);
      this.closed.emit();
    }
  }
}
